import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { startLoading, stopLoading } from "../loading";
import { BASE_URL, getById, postCall } from "../../utils/api";
import toast from "react-hot-toast";
import axios from "axios";

const initialState = {
  user: JSON.parse(localStorage.getItem("token")) || null,
  userDetails: null,
  data: [],
  error: "",
  status: "idle",
};

export const login = createAsyncThunk(
  "auth/login",
  async (credentials, { dispatch, rejectWithValue }) => {
    dispatch(startLoading());
    try {
      const response = await postCall("/api/v1/login", credentials, false);
      const expiryTime = Date.now() + 3 * 24 * 60 * 60 * 1000;
      localStorage.setItem("expiryTime", expiryTime);
      dispatch(stopLoading());
      return await response;
    } catch (error) {
      dispatch(stopLoading());
      return rejectWithValue(error?.response?.data?.message);
    }
  }
);

export const requestOtp = createAsyncThunk(
  "auth/request_otp",
  async (email, { dispatch }) => {
    dispatch(startLoading());
    try {
      const response = await postCall("/api/v1/request_otp", { email });
      dispatch(stopLoading());
      return response;
    } catch (error) {
      dispatch(stopLoading());
      throw error;
    }
  }
);

export const reSendOtp = createAsyncThunk(
  "auth/reSend_otp",
  async (email, { dispatch }) => {
    dispatch(startLoading());
    try {
      const response = await postCall("/api/v1/resend_otp", { email });
      dispatch(stopLoading());
      return response;
    } catch (error) {
      dispatch(stopLoading());
      throw error;
    }
  }
);

export const forgotPassword = createAsyncThunk(
  "auth/forgotPassword",
  async (data, { dispatch }) => {
    dispatch(startLoading());
    try {
      const response = await postCall("/api/v1/forgot_password", data);
      dispatch(stopLoading());
      return response;
    } catch (error) {
      dispatch(stopLoading());
      throw error;
    }
  }
);

export const otpVerify = createAsyncThunk(
  "auth/otpVerify",
  async (data, { dispatch }) => {
    dispatch(startLoading());
    try {
      const response = await postCall("/api/v1/verify_otp", data);
      dispatch(stopLoading());
      return response;
    } catch (error) {
      dispatch(stopLoading());
      throw error;
    }
  }
);

export const changePassword = createAsyncThunk(
  "auth/changePassword",
  async (formData, { rejectWithValue }) => {
    const token = JSON.parse(localStorage.getItem("token"));
    try {
      const response = await axios.post(
        `${BASE_URL}/api/v1/reset_password`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${token}`,
          },
        }
      );
      toast.success(response.data.message || "change password successfully!");
      return response.data;
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to update card");
      return rejectWithValue(error.response.data);
    }
  }
);
export const updateProfile = createAsyncThunk(
  "auth/updateProfile",
  async (formData, { rejectWithValue }) => {
    const token = JSON.parse(localStorage.getItem("token"));
    try {
      const response = await axios.post(
        `${BASE_URL}/api/v1/update_profile`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${token}`,
          },
        }
      );
      toast.success(response.data.message || "profile updated successfully!");

      return response.data;
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to update profile");

      return rejectWithValue(error.response.data);
    }
  }
);
export const logout = createAsyncThunk(
  "auth/logout",
  async (_, { dispatch }) => {
    try {
      localStorage.removeItem("token");
      localStorage.removeItem("user_id");
      localStorage.removeItem("expiryTime");
      return;
    } catch (error) {
      throw error;
    }
  }
);

export const getAuthUserById = createAsyncThunk(
  "auth/getById",
  async (id, { dispatch }) => {
    dispatch(startLoading());
    try {
      const response = await getById("/admin/v1/show_user", id, false);
      return await response;
    } catch (error) {
      return Promise?.reject(error);
    } finally {
      dispatch(stopLoading());
    }
  }
);
const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    logout(state) {
      state.user = null;
      state.status = "idle";
      localStorage.removeItem("token");
      localStorage.removeItem("user_id");
      localStorage.removeItem("expiryTime");
    },
    rehydrateUserDetails(state, action) {
      state.userDetails = action.payload;
      state.data = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(login.pending, (state) => {
        state.status = "loading";
      })
      .addCase(login.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.data = action.payload;
        state.userDetails = action.payload;
      })
      .addCase(login.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.error.message || "Login failed";
      })
      .addCase(requestOtp.pending, (state) => {
        state.status = "loading";
      })
      .addCase(requestOtp.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.message = "OTP request sent successfully";
      })
      .addCase(requestOtp.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.error.message || "Failed to send OTP request";
      })
      .addCase(reSendOtp.pending, (state) => {
        state.status = "loading";
      })
      .addCase(reSendOtp.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.message = "OTP re-sent successfully";
      })
      .addCase(reSendOtp.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.error.message || "Failed to send OTP request";
      })
      .addCase(forgotPassword.pending, (state) => {
        state.status = "loading";
      })
      .addCase(forgotPassword.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.message = "Password reset instructions sent";
      })
      .addCase(forgotPassword.rejected, (state, action) => {
        state.status = "failed";
        state.error =
          action.error.message || "Failed to send password reset instructions";
      })
      .addCase(otpVerify.pending, (state) => {
        state.status = "loading";
      })
      .addCase(otpVerify.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.message = "OTP verified successfully";
        state.user = action.payload;
      })
      .addCase(otpVerify.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.error.message || "OTP verification failed";
      })
      .addCase(changePassword.pending, (state) => {
        state.status = "loading";
      })
      .addCase(changePassword.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.message = "Password changed successfully";
      })
      .addCase(changePassword.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.error.message || "Failed to change password";
      })
      .addCase(logout.fulfilled, (state) => {
        state.user = null;
        state.status = "idle";
      })
      .addCase(updateProfile.pending, (state) => {
        state.status = "loading";
      })
      .addCase(updateProfile.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.data = action.payload;
        state.userDetails = action.payload?.data;
      })
      .addCase(updateProfile.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.error.message || "An unknown error occurred";
      })
      .addCase(getAuthUserById.pending, (state) => {
        state.status = "loading";
      })
      .addCase(getAuthUserById.fulfilled, (state, action) => {
        state.status = "success";
        state.userDetails = action.payload?.data;
      })
      .addCase(getAuthUserById.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.error.message || "An unknown error occurred";
      });
  },
});

export const { logout: logoutAction, rehydrateUserDetails } = authSlice.actions;

export default authSlice.reducer;
